import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {Col, Form, Row} from "react-bootstrap";
import {useHistory, useLocation} from "react-router-dom";
import {WireTemplatesContext} from "./context";
import {SecurityContext} from "context/security";
import * as URL from "service/UrlConstant";
import {CLEARING_SYSTEM_CREATE} from "routes/Routes";
import WireTemplateWizardLayout from "./WireTemplateWizardLayout";
import ShiftEnterPopupMessage from "components/ShiftEnterPopupMessage";
import {ADD_NEW_CLEARING_SYSTEM_CODE_OPTION} from "./AddNewWireTemplateStep2";
import {
    getClearingSystemIdWithPrefix,
    getWireTemplateStepUrl,
    shouldNavigateToWireTemplateStep,
    USABA_CLEARING_SYSTEM_CODE,
    USABA_CLEARING_SYSTEM_ID_PREFIX,
    WIRE_TEMPLATE_STEP_URL
} from "./AddNewWireTemplateStep1";

const intermediaryAgentFields = [
    [
        {label: 'Agent BIC', name: 'intermediaryAgentBIC', md: 3, maxLength: 50, conditionallyRequired: true},
        {label: 'Clearing System Code', name: 'intermediaryAgentClearingSystemCode', md: 2, type: 'select', options: [{label: 'USABA', value: 'USABA'}], conditionallyRequired: true},
        {label: 'Clearing System ID', name: 'intermediaryAgentClearingSystemID', md: 4, type: 'select', dynamicOptions: true, conditionallyRequired: true},
        {label: 'Account IBAN', name: 'intermediaryAgentAccountIBAN', md: 3, maxLength: 50},
    ],
    [
        {label: 'Agent Name', name: 'intermediaryAgentName', md: 4, maxLength: 255},
        {label: 'Agent Department', name: 'intermediaryAgentDepartment', md: 4, maxLength: 70},
        {label: 'Agent Sub Department', name: 'intermediaryAgentSubDepartment', md: 4, maxLength: 70},
    ],
    [
        {label: 'Street Name', name: 'intermediaryAgentStreetName', md: 4, maxLength: 70},
        {label: 'Building Number', name: 'intermediaryAgentBuildingNumber', md: 2, maxLength: 16},
        {label: 'Building Name', name: 'intermediaryAgentBuildingName', md: 4, maxLength: 35},
        {label: 'Agent Floor', name: 'intermediaryAgentFloor', md: 2, maxLength: 70},
    ],
    [
        {label: 'Post Box', name: 'intermediaryAgentPostBox', md: 2, maxLength: 16},
        {label: 'Room', name: 'intermediaryAgentRoom', md: 2, maxLength: 70},
        {label: 'Post Code', name: 'intermediaryAgentPostCode', md: 2, maxLength: 16},
        {label: 'Town Name', name: 'intermediaryAgentTownName', md: 3, maxLength: 35, conditionallyRequired: true},
        {label: 'Town Location Name', name: 'intermediaryAgentTownLocationName', md: 3, maxLength: 35},
    ],
    [
        {label: 'District Name', name: 'intermediaryAgentDistrictName', md: 5, maxLength: 35},
        {label: 'Province', name: 'intermediaryAgentProvince', md: 4, maxLength: 35},
        {label: 'Country', name: 'intermediaryAgentCountry', md: 3, maxLength: 2, type: 'country', conditionallyRequired: true},
    ],
];

const INTERMEDIARY_AGENT_IDENTIFIER_ERROR = 'ERROR. Please check the Note above to fill in the required fields.';
const INTERMEDIARY_AGENT_CLEARING_SYSTEM_ID_FORMAT_ERROR = `Clearing System ID must start with ${USABA_CLEARING_SYSTEM_ID_PREFIX} followed by digits`;

const hasValue = (value) => value !== undefined && value !== null && value.toString().trim() !== '';
const isValidUSABAClearingSystemID = (clearingSystemID) => new RegExp(`^${USABA_CLEARING_SYSTEM_ID_PREFIX}\\d+$`).test(clearingSystemID.toString().trim());

export const getIntermediaryAgentClearingSystemIDWithPrefix = (clearingSystemID) => {
    if (!clearingSystemID) {
        return USABA_CLEARING_SYSTEM_ID_PREFIX;
    }

    const clearingSystemIDValue = clearingSystemID.toString();
    const clearingSystemIDValueUpperCase = clearingSystemIDValue.toUpperCase();

    if (USABA_CLEARING_SYSTEM_ID_PREFIX.startsWith(clearingSystemIDValueUpperCase) && clearingSystemIDValueUpperCase.length < USABA_CLEARING_SYSTEM_ID_PREFIX.length) {
        return USABA_CLEARING_SYSTEM_ID_PREFIX;
    }

    return getClearingSystemIdWithPrefix(clearingSystemIDValue);
}

export const validateStep5 = (addWireTemplateState = {}) => {
    let errors = {}
    const {
        intermediaryAgentBIC,
        intermediaryAgentClearingSystemCode,
        intermediaryAgentClearingSystemID,
        intermediaryAgentName,
        intermediaryAgentTownName,
        intermediaryAgentCountry,
    } = addWireTemplateState;

    const hasIntermediaryAgentName = hasValue(intermediaryAgentName);

    const hasBIC = hasValue(intermediaryAgentBIC);
    const hasClearingSystemCode = hasValue(intermediaryAgentClearingSystemCode);
    const hasClearingSystemID = hasValue(intermediaryAgentClearingSystemID);
    const hasTown = hasValue(intermediaryAgentTownName);
    const hasCountry = hasValue(intermediaryAgentCountry);

    if (hasIntermediaryAgentName && !hasBIC && (!hasClearingSystemCode || !hasClearingSystemID || !hasTown || !hasCountry)) {
        errors['intermediaryAgentBIC'] = INTERMEDIARY_AGENT_IDENTIFIER_ERROR;

        if (!hasClearingSystemCode) {
            errors['intermediaryAgentClearingSystemCode'] = INTERMEDIARY_AGENT_IDENTIFIER_ERROR;
        }

        if (!hasClearingSystemID) {
            errors['intermediaryAgentClearingSystemID'] = INTERMEDIARY_AGENT_IDENTIFIER_ERROR;
        }

        if (!hasTown) {
            errors['intermediaryAgentTownName'] = INTERMEDIARY_AGENT_IDENTIFIER_ERROR;
        }

        if (!hasCountry) {
            errors['intermediaryAgentCountry'] = INTERMEDIARY_AGENT_IDENTIFIER_ERROR;
        }
    }

    if (intermediaryAgentClearingSystemCode === USABA_CLEARING_SYSTEM_CODE && hasClearingSystemID && !isValidUSABAClearingSystemID(intermediaryAgentClearingSystemID)) {
        errors['intermediaryAgentClearingSystemID'] = INTERMEDIARY_AGENT_CLEARING_SYSTEM_ID_FORMAT_ERROR
    }

    return errors
}

export default function AddNewWireTemplateStep5() {

    const history = useHistory();
    const location = useLocation();
    const {addWireTemplateState, setAddWireTemplateState, clearAddNewWireTemplateState} = useContext(WireTemplatesContext);
    const {makeGetRequest, makeGetRequestNoParams} = useContext(SecurityContext);
    const [countries, setCountries] = useState([]);
    const [clearingSystemList, setClearingSystemList] = useState([]);
    const [formErrors, setFormErrors] = useState({});
    // Cache of clearing system id lists keyed by clearing system code so the API
    // is only called once per code (avoids re-fetching on every code change).
    const clearingSystemCacheRef = useRef({});

    const currentStepPage = '5'
    const backStepPage = '4'
    const nextStepPage = '6'

    const getCountries = useCallback(() => {
        const onSuccessResponse = (res) => {
            if (res.data) {
                setCountries(res.data);
            }
        }

        const onErrorResponse = () => {}

        makeGetRequestNoParams(URL.COUNTRY_LIST_COUNTRY_NAME, onSuccessResponse, onErrorResponse)
    }, [makeGetRequestNoParams])

    const getClearingSystemList = useCallback((clearingSystemCode) => {
        if (!clearingSystemCode) {
            setClearingSystemList([]);
            return;
        }

        // Serve from cache if this clearing system code was already fetched.
        if (clearingSystemCacheRef.current[clearingSystemCode]) {
            setClearingSystemList(clearingSystemCacheRef.current[clearingSystemCode]);
            return;
        }

        const onSuccessResponse = (res) => {
            const data = res.data || [];
            clearingSystemCacheRef.current[clearingSystemCode] = data;
            setClearingSystemList(data);
        }

        const onErrorResponse = (err) => {
            console.log("Error on get clearing system ids by system code: ", err);
        }

        makeGetRequest(URL.CLEARING_SYSTEM_FIND_SYSTEM_IDS_BY_SYSTEM_CODE_URL, {clearingSystemCode}, onSuccessResponse, onErrorResponse)
    }, [makeGetRequest])

    // Fetch the clearing system details for the selected Clearing System Code and ID
    // and populate the intermediary agent name, town, province and country fields.
    const getClearingSystemDetails = useCallback((clearingSystemCode, clearingSystemId) => {
        if (!clearingSystemCode || !clearingSystemId) {
            return;
        }

        const onSuccessResponse = (res) => {
            const data = res.data;
            if (data) {
                setAddWireTemplateState(previousState => ({
                    ...previousState,
                    intermediaryAgentName: data.agentName || '',
                    intermediaryAgentTownName: data.agentCity || '',
                    intermediaryAgentProvince: data.agentProvince || '',
                    intermediaryAgentCountry: data.agentCountry || '',
                }));
            }
        }

        const onErrorResponse = (err) => {
            console.log("Error on get clearing system by system code and system id: ", err);
        }

        makeGetRequest(URL.CLEARING_SYSTEM_FIND_BY_SYSTEM_CODE_AND_SYSTEM_ID_URL, {clearingSystemCode, clearingSystemId}, onSuccessResponse, onErrorResponse)
    }, [makeGetRequest, setAddWireTemplateState])

    // Build the Clearing System ID dropdown options from the list returned by the API
    // (already filtered by the selected Clearing System Code). For USABA, keep the FW prefix.
    const clearingSystemIdOptions = useMemo(() => {
        const selectedCode = addWireTemplateState.intermediaryAgentClearingSystemCode;
        if (!selectedCode) {
            return [];
        }

        return clearingSystemList
            .map(item => {
                const clearingSystemId = selectedCode === USABA_CLEARING_SYSTEM_CODE
                    ? getIntermediaryAgentClearingSystemIDWithPrefix(item.clearingSystemId)
                    : item.clearingSystemId;
                return {label: clearingSystemId, value: clearingSystemId};
            });
    }, [clearingSystemList, addWireTemplateState.intermediaryAgentClearingSystemCode])

    useEffect(() => {
        getCountries()
    }, [getCountries])
    useEffect(() => {
        getClearingSystemList(addWireTemplateState.intermediaryAgentClearingSystemCode)
    }, [getClearingSystemList, addWireTemplateState.intermediaryAgentClearingSystemCode])
    useEffect(() => {
        if (shouldNavigateToWireTemplateStep(currentStepPage, addWireTemplateState?.newPageStep)) {
            history.push(getWireTemplateStepUrl(addWireTemplateState.newPageStep))
        }
    }, [addWireTemplateState?.newPageStep, history])

    // When returning from the Create Clearing System screen, pre-populate the
    // Intermediary Agent Clearing System Code and ID with the values that were just
    // created, along with the Agent Name/City/Country entered on the create
    // screen so the user doesn't have to re-enter them here. The component
    // remounts on return, so the ID dropdown refetches from the API (now
    // including the new record) and can display the selected id.
    useEffect(() => {
        const createdClearingSystem = location.state && location.state.createdClearingSystem;
        if (createdClearingSystem && createdClearingSystem.clearingSystemCode) {
            setAddWireTemplateState(previousState => ({
                ...previousState,
                intermediaryAgentClearingSystemCode: createdClearingSystem.clearingSystemCode,
                intermediaryAgentClearingSystemID: createdClearingSystem.clearingSystemId,
                intermediaryAgentName: createdClearingSystem.agentName || previousState.intermediaryAgentName,
                intermediaryAgentTownName: createdClearingSystem.agentCity || previousState.intermediaryAgentTownName,
                intermediaryAgentProvince: createdClearingSystem.agentProvince || previousState.intermediaryAgentProvince,
                intermediaryAgentCountry: createdClearingSystem.agentCountry || previousState.intermediaryAgentCountry,
            }));
            // Clear the navigation state so the values are not re-applied on re-render.
            history.replace(location.pathname);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleChange = (e) => {
        const {name, value} = e.currentTarget;

        // Selecting the special "Add new clearing system code" option in the
        // Clearing System ID dropdown navigates to the Create Clearing System screen
        // instead of updating the field value.
        if (name === 'intermediaryAgentClearingSystemID' && value === ADD_NEW_CLEARING_SYSTEM_CODE_OPTION) {
            goToAddNewClearingSystemCode();
            return;
        }

        const newAddWireTemplateState = {...addWireTemplateState, [name]: value};

        // Clearing System ID options depend on the selected Clearing System Code,
        // so reset the previously selected ID whenever the code changes.
        if (name === 'intermediaryAgentClearingSystemCode') {
            newAddWireTemplateState.intermediaryAgentClearingSystemID = '';
        }

        setAddWireTemplateState(newAddWireTemplateState);
        if (formErrors[name]) {
            setFormErrors(previousErrors => ({...previousErrors, [name]: ''}))
        }
        if (name === 'intermediaryAgentName') {
            setFormErrors(validateStep5(newAddWireTemplateState))
        }

        // When a Clearing System ID is selected with a non-empty value, fetch the
        // clearing system details and populate the intermediary agent name, town,
        // province and country fields. When the value is cleared, reset those fields.
        if (name === 'intermediaryAgentClearingSystemID') {
            if (hasValue(value)) {
                getClearingSystemDetails(newAddWireTemplateState.intermediaryAgentClearingSystemCode, value);
            } else {
                setAddWireTemplateState(previousState => ({
                    ...previousState,
                    intermediaryAgentName: '',
                    intermediaryAgentTownName: '',
                    intermediaryAgentProvince: '',
                    intermediaryAgentCountry: '',
                }));
            }
        }
    }


    const handleClear = (e) => {
        e.preventDefault();
        clearAddNewWireTemplateState();
        setFormErrors({});
    }

    const handleBack = (e) => {
        e.preventDefault();
        const newAddWireTemplateState = {...addWireTemplateState}
        newAddWireTemplateState['newPageStep']=backStepPage
        setAddWireTemplateState(newAddWireTemplateState)
        history.push(WIRE_TEMPLATE_STEP_URL + backStepPage);
    }

    // Navigate to the Create Clearing System screen, remembering this step so the
    // user is returned here after a successful create. The wizard field values live
    // in WireTemplatesContext (and session storage), so they survive the round trip.
    const goToAddNewClearingSystemCode = () => {
        history.push(CLEARING_SYSTEM_CREATE, {returnTo: location.pathname});
    }

    const handleNext = (e) => {
        e.preventDefault();

        const errors = validateStep5(addWireTemplateState);
        if (Object.keys(errors).length !== 0) {
            setFormErrors(errors);
            return
        }

        const newAddWireTemplateState = {...addWireTemplateState}
        newAddWireTemplateState['newPageStep']=nextStepPage
        setAddWireTemplateState(newAddWireTemplateState)
        history.push(WIRE_TEMPLATE_STEP_URL + nextStepPage);
    }

    const renderRequiredMarker = (field) => {
        if (field.required) {
            return <span className='red-cell'>*</span>;
        }

        return field.conditionallyRequired && hasValue(addWireTemplateState.intermediaryAgentName) ? <span className='grey-cell'>*</span> : null;
    };

    const renderField = (field) => (
        <Col md={field.md} key={field.name}>
            <Form.Group controlId={`formWireTemplateStep6${field.name}`} className={field.type === 'country' || field.type === 'select' ? 'td-select' : ''}>
                <Form.Label column={false} className="filter-input-label">{field.label} {renderRequiredMarker(field)}</Form.Label>
                {field.type === 'country' ?
                    <Form.Control as="select" name={field.name} value={addWireTemplateState[field.name] || ''} onChange={handleChange}
                                  isInvalid={!!formErrors[field.name]}>
                        <option value={''}>Select</option>
                        {countries.map(country => (
                            <option key={country.countryCode} value={country.countryCode}>{country.country}</option>
                        ))}
                    </Form.Control> : field.type === 'select' ?
                    <Form.Control as="select" name={field.name} value={addWireTemplateState[field.name] || ''} onChange={handleChange}
                                  isInvalid={!!formErrors[field.name]}>
                        {field.name === 'intermediaryAgentClearingSystemID' &&
                            <option value={ADD_NEW_CLEARING_SYSTEM_CODE_OPTION} style={{color: '#008a00', fontWeight: 'bold'}}>Add new clearing system code &gt;</option>
                        }
                        <option value={''}>Select</option>
                        {(field.dynamicOptions ? clearingSystemIdOptions : field.options).map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </Form.Control> :
                    <Form.Control
                        name={field.name}
                        value={addWireTemplateState[field.name] || ''}
                        maxLength={field.maxLength}
                        onChange={handleChange}
                        isInvalid={!!formErrors[field.name]}
                    />
                }
                <Form.Control.Feedback type={'invalid'}>
                    <b><i className="bi bi-exclamation-circle"></i>{formErrors[field.name]}</b>
                </Form.Control.Feedback>
            </Form.Group>
        </Col>
    )

    const hasFormErrors = Object.keys(formErrors).some(Boolean);

    return (
        <WireTemplateWizardLayout currentStep={5} stepTitle="Intermediary Agent Details">
            <ShiftEnterPopupMessage />
            <Form>
                <div className={`awt-note ${hasFormErrors ? 'awt-note--error' : ''}`} role="note">
                    <b>Note:</b> If IntermediaryAgentName is populated please fill in IntermediaryAgentBIC OR IntermediaryAgentClearingSystemCode, IntermediaryAgentClearingSystemIDs,  IntermediaryAgentTown, and IntermediaryAgentCountry.
                    <p></p>
                    <b>Note:</b> Please populate Intermediary Agent Street Name and Intermediary Agent Province where possible.
                </div>
                {intermediaryAgentFields.map((fieldRow, index) => (
                    <Row key={index}>
                        {fieldRow.map(renderField)}
                    </Row>
                ))}

                <div className="awt-actions">
                    <button className="btn td-btn-secondary-clear" onClick={handleClear}>Clear</button>
                    <button className="btn td-btn-secondary-clear" onClick={handleBack}>Back</button>
                    <button className="btn td-btn-primary-light" onClick={handleNext}>Next</button>
                </div>
            </Form>
        </WireTemplateWizardLayout>
    )
}


